import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import axios from "axios";
import { bookingView } from "../../store/slice/features/bookingSlice";
import './HotelInvoice.css';
import { REACT_APP_API_URL } from "../../env";
import PartialDayCancellationTable from "./PartialDayCancellationTable";

const cancellationPolicy = [
    { id: 1, window: "15 or more days", deduction: "20%", description: "20% deduction if cancelled 15 or more days before check-in Date" },
    { id: 2, window: "7 to 14 days", deduction: "30%", description: "30% deduction if cancelled between 7 to 14 days before check-in Date" },
    { id: 3, window: "2 to 6 days", deduction: "50%", description: "50% deduction if cancelled between 2 to 6 days before check-in Date" },
    { id: 4, window: "Less than 2 days", deduction: "No cancellation allowed", description: "No cancellation allowed within 2 days of check-in Date", isCritical: true },
];

const HotelInvoice = () => {
    const { P_intBookingID } = useParams();

    const [selectedBooking, setSelectedBooking] = useState(null);
    const [selectedPayment, setSelectedPayment] = useState([]);
    const [partialDayCancellationData, setPartialDayCancellationData] = useState([]);
    const [partialDayCancellationLoading, setPartialDayCancellationLoading] = useState(false);
    const [partialDayCancellationError, setPartialDayCancellationError] = useState("");

    const [totalAmount, setTotalAmount] = useState("0.00");
    const dispatch = useDispatch();

    useEffect(() => {
        const fetchReceipt = async () => {
            try {
                const response = await axios.get(
                    `${REACT_APP_API_URL}/api/dashboard/admin/booking-view/${P_intBookingID}`
                );
                setSelectedBooking(response.data);
            } catch (error) {
                console.error(error.message, "error occurred");
            }
        };
        fetchReceipt();
    }, [P_intBookingID]);
    useEffect(() => {
        dispatch(bookingView(P_intBookingID));
    }, [dispatch, P_intBookingID]);

    useEffect(() => {
        const fetchPartialDayCancellation = async () => {
            setPartialDayCancellationLoading(true);
            setPartialDayCancellationError("");
            try {
                const response = await axios.get(
                    `${REACT_APP_API_URL}/api/dashboard/admin/booking-modification-log/${P_intBookingID}`
                );

                const payload = response?.data;
                const records = Array.isArray(payload) ? payload : Array.isArray(payload?.data) ? payload.data : [];
                
                setPartialDayCancellationData(records);
            } catch (error) {
                console.error("Error fetching partial day cancellation details:", error);
                setPartialDayCancellationData([]);
                setPartialDayCancellationError(
                    error?.response?.data?.message || "Failed to fetch partial day cancellation records"
                );
            } finally {
                setPartialDayCancellationLoading(false);
            }
        };

        if (P_intBookingID) {
            fetchPartialDayCancellation();
        }
    }, [P_intBookingID]);

    useEffect(() => {
        if (selectedBooking?.data?.length > 0) {
            const amount = selectedBooking.data
                .reduce((acc, booking) => acc + (parseFloat(booking.Amount) || 0), 0)
                .toFixed(2);
            setTotalAmount(amount);

            const fetchBookingPayment = async () => {
                try {
                    const response = await axios.get(
                        `${REACT_APP_API_URL}/api/dashboard/admin/booking-payment/${P_intBookingID}/${amount}`
                    );
                    console.log("Booking Payment Response:", response.data);
                    setSelectedPayment(response.data.data);
                } catch (error) {
                    console.error("Error fetching booking payment:", error);
                }
            };

            fetchBookingPayment();
        }
    }, [selectedBooking, P_intBookingID]);

    const roomTypeByRoomBookingId = useMemo(() => {
        const map = {};
        if (Array.isArray(selectedBooking?.data)) {
            selectedBooking.data.forEach((booking) => {
                const id =
                    booking?.intRoomBookingID ??
                    booking?.RoomBookingID ??
                    booking?.roomBookingID ??
                    booking?.RoomBookingId ??
                    booking?.RoomID ??
                    booking?.roomId;
                if (id !== undefined && id !== null && id !== "") {
                    const roomType =
                        booking?.RoomTypeName ||
                        booking?.vchRoomTypeName ||
                        booking?.RoomType ||
                        booking?.vchRoomType ||
                        "";
                    if (roomType) {
                        map[String(id)] = roomType;
                    }
                }
            });
        }
        return map;
    }, [selectedBooking]);

    if (
        !selectedBooking ||
        !selectedBooking.data ||
        !Array.isArray(selectedBooking.data)
    ) {
        return <p>Loading receipt...</p>;
    }

    const rawBillDate = selectedBooking.data[0].BillDate || "";
    let formattedDate = "";
    let formattedTime = "";

    if (rawBillDate) {
        const [datePart, timePart] = rawBillDate.split(" ");
        formattedDate = datePart;
        formattedTime = timePart;
    }

    return (
        <div className="invoice-container container mt-4 mb-4">
            <div className="border p-4 shadow-sm">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="row">
                        <div className="col-sm-3 col-xs-3 sm3">
                            <center className="image">
                                <img src="/images/logo/JTDC2.png" alt="JTDC Logo" height="125px" width="220px" />
                            </center>
                        </div>
                        <div className="col-sm-6 col-xs-6">
                            <div className="maindiv print-center">
                                <h3><strong>Jharkhand Tourism Development Corporation Ltd.</strong></h3>
                                <h6>A Govt. of Jharkhand Undertaking</h6>
                                <p>
                                    (6th Floor, Premium Suit Building, JUPMI Campus, Plant Plaza Road, Dhurwa, Ranchi - 834004)
                                </p>
                                <p>
                                    <span id="lblServicetaxNo" style={{ color: "Black" }}>
                                        GST Identification Number (GSTIN): 20AABCJ4014D6ZD
                                    </span>
                                </p>
                            </div>
                        </div>
                        <div className="col-sm-3 col-xs-3 sm3">
                            <center>
                                <img
                                    src="/images/logo/TourismLogo1.webp"
                                    alt="Department of Tourism"

                                />
                            </center>
                        </div>
                    </div>
                </div>

                <div className="header-bottom">
                    <div className="row align-items-center justify-content-between">
                        <div className="col-auto">
                            <div className="header-bottom_left">
                                <p>
                                    <span className="fw-bold fs-6">
                                        <b>Guest Name : </b>
                                        {selectedBooking.data[0].CustomerName || ""}
                                    </span>
                                </p>
                                <div className="shape"></div>
                                <div className="shape"></div>
                            </div>
                        </div>
                        <div className="col-auto">
                            <div className="header-bottom_right">
                                <div className="shape"></div>
                                <div className="shape"></div>
                                <p>
                                    <span className="fw-bold fs-6">
                                        <b>Booking Ref: </b>
                                        {selectedBooking.data[0].BookingRefNo || ""}
                                    </span>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Booking Info */}
                <div className="row justify-content-between">
                    <div className="col-auto">
                        <div className="booking-info">
                            <p>
                                <b>Booking Date: </b>  {formattedDate}
                            </p>
                            <p>
                                <b>Booking Time: </b> {formattedTime}
                            </p>
                            <p>
                                <b>Booking Type: </b> {selectedBooking.data[0].ChildAge1 || ""}
                            </p>
                        </div>
                    </div>
                    <div className="col-auto">
                        <div className="booking-info">
                            <p>
                                <b>Booking Day(s): </b> {selectedBooking.data[0].NoOfDay || ""}
                            </p>
                            <p>
                                <b>Check-In Date: </b> {selectedBooking.data[0].CheckinDate || ""}
                            </p>
                            <p>
                                <b>Check-Out Date: </b> {selectedBooking.data[0].CheckOutDate || ""}
                            </p>
                        </div>
                    </div>
                    <div className="col-auto">
                        <div className="booking-info">
                            <p>
                                <b>No of Room(s) Booked: </b> {selectedBooking.data.length || "10"}
                            </p>
                            <p>
                                <b>Payment Mode: </b> {selectedBooking.data[0].Age || ""}
                            </p>
                            <p>
                                <b>Transaction No:
                                </b> {selectedBooking.data[0].PaymentMode || ""}
                            </p>

                        </div>
                    </div>
                </div>

                {/* Addresses */}
                <div className="row gx-0">
                    <div className="col-6">
                        <div className="address-box address-left" style={{ wordBreak: 'break-word' }}>
                            <h6>Guest Information:</h6>

                            <b>Mobile No: </b>{selectedBooking.data[0].MobileNo || ""} <br />
                            <b>Email-Id: </b>{selectedBooking.data[0].EmailID || ""} <br />
                            <b>Address: </b>
                            <span style={{ display: "inline-block", wordBreak: "break-word" }}>
                                {selectedBooking.data[0].Address || ""}
                            </span>
                            <br />
                        </div>
                    </div>
                    <div className="col-6">
                        <div className="address-box address-right">
                            <h6>Hotel Details:</h6>
                            {selectedBooking?.data?.length > 0 ? (
                                <div>
                                    <b>Unit Name: </b>{selectedBooking.data[0].PropertyName || "N/A"}<br />
                                    <b>MR No: </b>{selectedBooking.data[0].vchMRNumber || "N/A"} <br />
                                    <b>MR Date: </b>{selectedBooking.data[0].ChildAge2 || "N/A"}
                                </div>

                            ) : (
                                <p>No booking details available.</p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Table */}
                <h5>Room Booking Summary</h5>
                <div className="table-responsive">
                    <table className="table table-bordered table-hover shadow-sm rounded text-center align-middle" style={{ fontSize: "0.85rem" }}>
                        <thead className="bg-light text-dark fw-semibold">
                            <tr>
                                <th>Sl#</th>
                                <th>Room Type</th>
                                <th>Adult</th>
                                <th>Child</th>
                                <th>Base Price (₹)</th>
                                <th>Extra Person</th>
                                <th>Extra Person Rate (₹)</th>
                                <th>Base Price * No. of Days(₹)</th>
                                <th>Tax %</th>
                                <th>Tax Amt (₹)</th>
                                <th>Grand Total (₹)</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>

                            {selectedBooking?.data?.length > 0 ? (
                                selectedBooking?.data?.map((booking, index) => (
                                    <tr key={index}>
                                        <td>
                                            <span>{index + 1}</span>
                                        </td>
                                        <td>
                                            {booking.RoomTypeName || ""}
                                        </td>
                                        <td>
                                            {booking.NoOfPerson || 0}
                                        </td>
                                        <td>
                                            {booking.NoOfChild || 0}
                                        </td>
                                        <td>
                                            {booking.PricePerDay || "0.00"}
                                        </td>
                                        <td>
                                            {booking.ExtraPerson || 0}
                                        </td>
                                        <td>
                                            {booking.ExtraPersonRate || "0.00"}
                                        </td>
                                        <td>
                                            {booking.Amount || "0.00"}
                                        </td>
                                        <td>
                                            {booking.TaxType || "N/A"}
                                        </td>
                                        <td>
                                            {booking.TotalAmount || "0.00"}
                                        </td>
                                        <td>
                                            {booking.TaxPercentage || "0.00"}
                                        </td>
                                        <td>
                                            <span className={`badge ${booking.Remarks === "Cancelled" ? "bg-danger" : "bg-primary"}`}>
                                                {booking.Remarks || ""}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="13" style={{ textAlign: "center" }}>
                                        No Booking Details Found
                                    </td>
                                </tr>
                            )}
                            <tr className="table-success fw-bold bg-booking-highlight">
                                <td colSpan="10" className="text-center">Total Amount Paid (Base + Extra + GST):</td>
                                <td>
                                    {selectedBooking?.data
                                        ?.reduce(
                                            (acc, booking) =>
                                                acc +
                                                (parseFloat(booking.Amount) || 0) +
                                                (parseFloat(booking.TotalAmount) || 0),
                                            0
                                        )
                                        .toFixed(2) || "0.00"}
                                </td>
                                <td></td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                {!partialDayCancellationLoading && partialDayCancellationData.length > 0 && (
                    <PartialDayCancellationTable
                        data={partialDayCancellationData}
                        loading={partialDayCancellationLoading}
                        error={partialDayCancellationError}
                        originalRoomType={selectedBooking?.data?.[0]?.RoomTypeName || "-"}
                        roomTypeByRoomBookingId={roomTypeByRoomBookingId}
                    />
                )}

                {/* Notes & Totals */}
                <div className="row justify-content-between mb-4">
                    <div className="col-md-4">
                        <h6>Tax Breakdown</h6>
                        <table className="table table-bordered table-striped shadow-sm rounded text-center" style={{ fontSize: "0.75rem" }}>
                            <thead>
                                <tr>
                                    <th>Tax Type</th>
                                    <th>Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {selectedPayment?.map((payment, index) => (
                                    <tr key={index}>
                                        <td>{payment.TaxType || "N/A"}</td>
                                        <td>
                                            {payment.TaxAmount != null
                                                ? parseFloat(payment.TaxAmount).toFixed(2)
                                                : "0.00"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="col-md-8">
                        <h6 id="Cancellation-table-heading">Cancellation Policy</h6>
                        <table
                            className="cancellation-table"
                            style={{ fontSize: "0.85rem" }}
                        >
                            <thead>
                                <tr>
                                    <th>Sl#</th>
                                    <th>Cancellation Window</th>
                                    <th>Deduction</th>
                                    <th>Description</th>
                                </tr>
                            </thead>
                            <tbody>
                                {cancellationPolicy.map((policy) => (
                                    <tr key={policy.id}>
                                        <td>{policy.id}</td>
                                        <td>{policy.window}</td>
                                        <td style={policy.isCritical ? { color: "red" } : {}}>
                                            {policy.deduction}
                                        </td>
                                        <td style={policy.isCritical ? { color: "red" } : {}}>
                                            {policy.description}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
                <div className="col-md-12 p-4 note-section">
                    <h6>Please Note:</h6>
                    <ul className="ul-with-bullets" style={{ fontSize: "1rem" }}>
                        <li>
                            This is an electronically generated receipt and is valid only when accompanied by an original government-issued photo ID such as Voter ID, Aadhaar, Passport, Driving License, or a Student ID issued by a recognized school or college.
                        </li>
                        <li>
                            Please show this receipt along with your valid ID proof at the time of hotel booking or check-in. Without both, entry may be denied.
                        </li>
                        <li>
                            The standard check-in time is <strong>12:00 PM</strong>, and check-out is at <strong>11:00 AM</strong>. Early check-in or late check-out may be subject to availability and additional charges.
                        </li>
                        <li>
                            If your booking includes extra guests, please share their name, age, and relationship with you at the time of check-in with the hotel manager.
                        </li>
                        <li>
                            Bookings made are non-transferable and cannot be shifted to another person.
                        </li>
                        <li>
                            Cancellations are allowed according to the JTDC cancellation policy. Please check the policy details before making changes.
                        </li>
                        <li>
                            Any refund, if applicable, will be credited back to the same account used during booking. Please ensure the account remains active. Room tariffs are subject to revision without prior notice.
                        </li>
                        <li>
                            For any help or assistance, feel free to contact the Hotel Manager or the nearest Tourist Information Centre (TIC).
                        </li>
                    </ul>
                </div>

                {/* Footer Note */}
                <p className="invoice-note mt-3 text-center company-address" style={{ fontSize: "0.85rem" }}>
                    <svg width="14" height="18" viewBox="0 0 14 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="" fill="#2D7CFE" />
                    </svg>
                    <b>NOTE: </b>This is computer generated receipt and does not require physical signature.
                </p>

                {/* Buttons */}
                <div className="d-flex gap-2 justify-content-end mt-4">
                    <button
                        className="btn btn-outline-success"
                        onClick={() => setTimeout(() => window.print(), 300)}
                        title="Print this invoice"
                    >
                        🖨️ Print
                    </button>
                </div>
            </div>
        </div >
    );
};

export default HotelInvoice;
